import React, { useContext } from 'react';
import { DateTime } from 'luxon';
import {
  maxCardSummaryLength,
  trimString,
  getPublicationDisplayTitle,
} from '../../../api/UtilsApi';
import PublicationLink from '../../publication/PublicationLink';
import {
  ContentAudience,
  Publication,
  WorkflowState,
} from '@amzn/awscat-pattern-library-service-model';
import {
  workflowStateToAudienceMap,
  BR_APPROVED_VALUE,
  barRaiserReviewStatusDisplayNames,
  workflowStateToVisibilityMap,
  contentAudienceDisplayNames,
} from '../../utils/DisplayNames';
import { Box, Icon } from '@amzn/awsui-components-core';
import FilterBadge, { FilterType } from './FilterBadge';
import { AppContext } from '../../../contexts/AppContext';
import { convertEntities } from '@amzn/awscat-editor';
import { replaceNBSPWithSpace } from '../../../api/ContentUtil';
import { useFeatureFlag } from '../../../contexts/FeatureFlagContext';
import { isBRApproved } from '../../utils/ContentFilterUtils';

interface Props {
  publication: Publication;
}

const PublicationCardView = ({ publication }: Props) => {
  const { contentId, workflowId, publicationState, lastUpdated } = publication;
  const appContext = useContext(AppContext);
  const isEarlySharingEnabled = useFeatureFlag('EarlySharing');
  const contentName = getPublicationDisplayTitle(publication);
  const contentType = publication.contentMetadata.contentType;

  const contentTypeClassName = `home-page-publication-content-type-${contentType.toLowerCase()}`;
  const publicationCardBorderTop = `home-page-publicatino-border-${contentType.toLowerCase()}`;
  const publicationDescription = replaceNBSPWithSpace(publication.contentMetadata.description);
  const isBrApproved = isBRApproved(publication);
  const brStatusLabel = barRaiserReviewStatusDisplayNames.get(BR_APPROVED_VALUE);

  const audienceValue = workflowStateToVisibilityMap.get(publicationState as WorkflowState);
  const visibilityIconName =
    audienceValue === contentAudienceDisplayNames.get(ContentAudience.INTERNAL) ? 'group' : 'globe';

  const visibilitySection = isEarlySharingEnabled ? (
    <div
      className="home-page-publication-card-visibility"
      style={{ display: 'flex', alignItems: 'center', marginLeft: '5px' }}
    >
      <Icon name={visibilityIconName} />
      <Box color="text-body-secondary" fontSize="body-s" margin={{ left: 'xs' }}>
        {audienceValue}
      </Box>
    </div>
  ) : (
    <div className="home-page-publication-card-visibility">
      <Box color="text-body-secondary" fontSize="body-s" margin={{ right: 'xs' }}>
        Visibility:
      </Box>
      <FilterBadge
        filterType={FilterType.AudienceType}
        filterText={publicationState}
        workflowStateToAudienceMap={workflowStateToAudienceMap}
        readOnly
      />
    </div>
  );

  return (
    <div className={`home-page-publication-card-container ${publicationCardBorderTop}`}>
      <div className="home-page-publication-card-body">
        <div className={contentTypeClassName}>{contentType}</div>
        <div className="home-page-publication-card-title">
          <PublicationLink
            contentName={contentName}
            contentId={contentId}
            workflowId={workflowId}
          />
        </div>
        <Box color="text-body-secondary" fontSize="body-s">
          {trimString(
            convertEntities(publicationDescription, appContext.entities),
            maxCardSummaryLength,
          )}
        </Box>
        {isEarlySharingEnabled ? (
          <Box color="text-body-secondary" margin={{ top: 'xs' }}>
            {lastUpdated.toLocaleString(DateTime.DATE_FULL)}
          </Box>
        ) : (
          visibilitySection
        )}
      </div>
      <div className="home-page-publication-card-bottom">
        {isEarlySharingEnabled ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {visibilitySection}
            {isBrApproved && (
              <div
                className="home-page-publication-card-br-status"
                style={{ display: 'flex', alignItems: 'center', marginRight: '5px' }}
              >
                <Icon name="status-positive" variant="success" />
                <Box
                  color="text-body-secondary"
                  fontSize="body-s"
                  margin={{ left: 'xs' }}
                  display="inline"
                >
                  <span style={{ whiteSpace: 'nowrap' }}>{brStatusLabel}</span>
                </Box>
              </div>
            )}
          </div>
        ) : (
          lastUpdated.toLocaleString(DateTime.DATE_FULL)
        )}
      </div>
    </div>
  );
};

export default PublicationCardView;
